import {
  Box,
  Checkbox,
  ConfirmationDialogBox,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { SelectInput, ToastType } from '@components';
import { Button } from '@components/Button';
import FormErrorComponent from '@components/forms/FormErrorComponent';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import {
  CarrierCSPAccount,
  carrierCSPCustomers,
  carrierDetailsStore,
  CarrierScac,
  CSPEnabledCustomerLookup,
  getCarrierCSPCustomers,
} from '@store/carriers';
import {
  createNewScacAccount,
  customerLTLSettings,
  EvaAccessIdRequirement,
  ScacAccount,
  updateExistingScacAccount,
} from '@store/customers/customerDetails/LTL';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { TestLane } from '@views/customer/customerDetail/ltl/components/TestLane';
import { createEffect, createSignal, onMount, Show } from 'solid-js';
import * as yup from 'yup';

type Props = {
  modalId: string;
  onModalClose: () => void;
  scacs: CarrierScac[];
  evaRequirements?: EvaAccessIdRequirement;
  selectedAccount: CarrierCSPAccount | undefined;
  billToName?: string;
  selectedCSPCustomer?: CSPEnabledCustomerLookup;
  customerId?: number;
  onSave?: () => void;
  createChangeTrackingSource?: ChangeTrackingSource;
  testLaneChangeTrackingSource?: ChangeTrackingSource;
  updateChangeTrackingSource?: ChangeTrackingSource;
};

const LTLPricingSetupEditModal = (props: Props) => {
  const [displayTestLane, setDisplayTestLane] = createSignal(false);
  const [testedLane, setTestedLane] = createSignal(false);
  const [overrideBillTo, setOverrideBillTo] = createSignal(false);
  const [originalBillToName, setOriginalBillToName] = createSignal<
    string | undefined
  >(props.billToName);
  const { form, setFields, data, errors, setIsSubmitting, isSubmitting } =
    createForm<Partial<CarrierCSPAccount>>({
      extend: validator({
        schema: yup.object({
          payerTypeId: yup.number().required('Payer Type is required'),
          termTypeId: yup.number().required('Term Type is required'),
          accountNumber: yup.string().required('Account Number is required'),
          serviceLevelTypes: yup
            .array()
            .transform((value) =>
              value === undefined || value === null
                ? []
                : (value as Array<unknown>),
            )
            .of(
              yup.object({
                id: yup.number().required(),
                displayName: yup.string().required(),
                code: yup.string().required(),
              }),
            )
            .test(
              'serviceLevelTypes-required',
              'Service Type is required',
              function (value) {
                if (isBlanketAccount(props)) {
                  return Array.isArray(value) && value.length > 0;
                }
                return true;
              },
            ),
          pricingRequestTypes: yup
            .array()
            .transform((value) =>
              value === undefined || value === null
                ? []
                : (value as Array<unknown>),
            )
            .of(
              yup.object({
                id: yup.number().required(),
                description: yup.string().required(),
              }),
            )
            .test(
              'pricingRequestTypes-required',
              'Pricing Request Type is required',
              function (value) {
                if (isBlanketAccount(props)) {
                  return Array.isArray(value) && value.length > 0;
                }
                return true;
              },
            ),
        }),
      }),
      onSubmit: async (values: Partial<CarrierCSPAccount>) => {
        try {
          setIsSubmitting(true);
          const withBillTo: Partial<CarrierCSPAccount> = {
            ...values,
            billToName: overrideBillTo() ? values.billToName : null,
          };
          if (props.evaRequirements?.isRequired === null) {
            handleToast(
              ToastType.Error,
              'EVA Access ID may be required for this carrier. Please contact IT for assistance.',
            );
          }

          if (
            props.evaRequirements?.isRequired === true &&
            (values.evaAccessId === undefined ||
              values.evaAccessId === '' ||
              props.evaRequirements.evaAccessId === null)
          ) {
            handleToast(
              ToastType.Error,
              'Please Submit a Valid EVA Access ID.',
            );
            return;
          }

          if (
            props.evaRequirements?.isRequired === true &&
            props.evaRequirements.isSet !== true
          ) {
            openDialogBox('AddNewEvaAccessIdConfirmationDialog');
            return;
          }
          await saveScacAccount(withBillTo);
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const saveScacAccount = async (values: Partial<CarrierCSPAccount>) => {
    // For blanket accounts, ensure customerId is explicitly null
    // For customer view, use the provided customerId
    const normalizedValues = {
      ...values,
      customerId: isBlanketAccount(props)
        ? null
        : (props.customerId ?? values.customerId),
      carrierId: values.carrierId ?? carrierDetailsStore.carrierDetails?.id,
    };

    const payload =
      props.selectedAccount && props.selectedAccount.id
        ? {
            ...props.selectedAccount,
            ...normalizedValues,
            id: props.selectedAccount.id,
          }
        : normalizedValues;

    const result =
      payload.id !== undefined
        ? await updateExistingScacAccount(
            payload,
            props.updateChangeTrackingSource,
          )
        : await createNewScacAccount(payload, props.createChangeTrackingSource);

    if (result) {
      // Call onSave callback if provided (customer view)
      if (props.onSave) {
        props.onSave();
      } else {
        // Default behavior for carrier view
        await getCarrierCSPCustomers(
          carrierDetailsStore.carrierDetails?.id as number,
        );
      }
      handleToast(
        ToastType.Success,
        `SCAC Account ${Boolean(payload.id) ? 'Updated' : 'Created'} Successfully`,
      );
      props.onModalClose();
    }

    setIsSubmitting(false);
  };

  onMount(() => {
    if (Boolean(props.selectedAccount)) {
      queueMicrotask(() => {
        setOriginalBillToName(props.selectedAccount?.billToName ?? undefined);
        setFields({
          payerTypeId: props.selectedAccount?.payerTypeId,
          payerTypeDescription: props.selectedAccount?.payerTypeDescription,
          termTypeId: props.selectedAccount?.termTypeId,
          termTypeDescription: props.selectedAccount?.termTypeDescription,
          evaAccessId: props.selectedAccount?.evaAccessId,
          accountNumber: props.selectedAccount?.accountNumber,
          billToName: props.selectedAccount?.billToName,
          customerId: props.customerId ?? props.selectedAccount?.customerId,
          carrierScacId: props.selectedAccount?.carrierScacId,
          carrierId: props.selectedAccount?.carrierId,
          scac: props.selectedAccount?.scac,
          carrierName: props.selectedAccount?.carrierName,
          customerName: props.selectedAccount?.customerName,
          serviceLevelTypes: props.selectedAccount?.serviceLevelTypes ?? [],
          pricingRequestTypes: props.selectedAccount?.pricingRequestTypes ?? [],
        });
      });
    }
  });

  function isBlanketAccount(props: Props) {
    // Customer view mode: never a blanket account
    if (props.customerId) {
      return false;
    }
    // Carrier view with selectedCSPCustomer: customer-specific, not blanket
    if (props.selectedCSPCustomer) {
      return false;
    }
    // No selected account and no customer: blanket account
    if (!props.selectedAccount) {
      return true;
    }
    // Check if the selected account has no customerId (blanket)
    return props.selectedAccount.customerId == null;
  }

  const blanket = carrierCSPCustomers.cspCustomers.find(
    (cust) => cust.customerId == null,
  );

  // Auto-populate customer and SCAC fields when selectedCSPCustomer changes
  createEffect(() => {
    if (props.selectedCSPCustomer) {
      if (blanket) {
        setFields({
          customerId: props.selectedCSPCustomer.customerId,
          customerName: props.selectedCSPCustomer.name,
          scac: blanket.scac,
          carrierScacId: blanket.carrierScacId,
        });
      }
    }
  });

  // Auto-populate for customer view mode when adding new account
  createEffect(() => {
    if (
      props.customerId &&
      !props.selectedAccount &&
      !props.selectedCSPCustomer &&
      props.scacs.length > 0
    ) {
      queueMicrotask(() => {
        const chosenCarrier = props.scacs[0];
        setFields({
          payerTypeId: undefined,
          termTypeId: undefined,
          evaAccessId: props.evaRequirements?.evaAccessId ?? '',
          accountNumber: '',
          customerId: props.customerId,
          billToName: props.billToName,
          carrierScacId: chosenCarrier.carrierScacId,
          carrierId: chosenCarrier.carrierId,
          scac: chosenCarrier.scac,
          carrierName: chosenCarrier.carrierName,
        });
      });
    }
  });

  const handleConfirmEvaAccessId = async () => {
    const values = {
      ...data(),
      billToName: overrideBillTo() ? data().billToName : null,
    };

    await saveScacAccount(values);
    closeDialogBox('AddNewEvaAccessIdConfirmationDialog');
    props.onModalClose();
  };

  return (
    <>
      <form ref={form} class="pt-2">
        <Box style={{ 'flex-grow': 1 }}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Stack spacing={2}>
                <Show
                  when={!props.customerId}
                  fallback={
                    <>
                      <TextField
                        label="SCAC"
                        value={data().scac ?? ''}
                        disabled
                        dataTestId="customer-specific-pricing-edit-modal-scac-textInput"
                      />
                      <TextField
                        label="Carrier Name"
                        value={data().carrierName ?? ''}
                        disabled
                        dataTestId="customer-specific-pricing-edit-modal-carrier-name-textInput"
                      />
                    </>
                  }
                >
                  <SelectInput
                    label="SCAC"
                    placeholder="Select SCAC"
                    menuItems={props.scacs.map((scac) => ({
                      label: scac.scac,
                      value: String(scac.carrierScacId),
                    }))}
                    value={
                      data().carrierScacId != null
                        ? String(data().carrierScacId)
                        : ''
                    }
                    onChange={(val) => {
                      setFields('carrierScacId', parseInt(val, 10));
                      setFields(
                        'scac',
                        props.scacs.find(
                          (scac) => scac.carrierScacId === parseInt(val, 10),
                        )?.scac,
                      );
                    }}
                    error={(() => {
                      const err = errors().carrierScacId;
                      if (err == null) return null;
                      if (Array.isArray(err)) return err[0] ?? null;
                      return typeof err === 'string' ? err : null;
                    })()}
                    disabled={
                      (props.selectedAccount &&
                        props.selectedAccount.customerId != null) ||
                      (props.selectedCSPCustomer &&
                        Boolean(props.selectedCSPCustomer.customerId))
                    }
                    dataTestId={formatDataTestId('CSP SCAC', 'selectInput')}
                  />
                  <TextField
                    label="Customer Name"
                    value={data().customerName ?? 'BLANKET'}
                    disabled={!Boolean(data().id)}
                    dataTestId="customer-specific-pricing-edit-modal-customer-name-textInput"
                  />
                </Show>
                <TextField
                  label="Account Number"
                  value={data().accountNumber ?? ''}
                  onChange={(e: string) => setFields('accountNumber', e)}
                  error={errors().accountNumber}
                  dataTestId="customer-specific-pricing-edit-modal-account-number-textInput"
                />
                <Show when={Boolean(props.evaRequirements?.isRequired)}>
                  <TextField
                    disabled={props.evaRequirements?.isSet ?? false}
                    label="EVA Access ID"
                    value={data().evaAccessId ?? ''}
                    onChange={(e: string) => setFields('evaAccessId', e)}
                    error={errors().evaAccessId}
                    dataTestId="customer-specific-pricing-edit-modal-eva-access-id-textInput"
                  />
                </Show>
                <Show
                  when={
                    Boolean(props.evaRequirements?.isRequired) &&
                    !Boolean(props.evaRequirements?.evaAccessId)
                  }
                >
                  <p class="text-sm text-red-600 mt-1">
                    An EVA Access ID is required before you are able to set up
                    Customer Specific Pricing for this customer. Please reach
                    out to IT for assistance.
                  </p>
                </Show>
                <Show when={props.evaRequirements?.isRequired === null}>
                  <p class="text-sm text-red-600 mt-1">
                    This carrier has not been set up with Customer Specific
                    Pricing before and may need an EVA Access ID. Please reach
                    out to IT for assistance.
                  </p>
                </Show>
              </Stack>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Stack spacing={2}>
                <SelectInput
                  label="Payer Type"
                  placeholder="Select Payer Type"
                  menuItems={customerLTLSettings.cspSetup.ltlScacAccountTypes.payerTypes.map(
                    (payerType) => ({
                      label: payerType.description,
                      value: String(payerType.id),
                    }),
                  )}
                  value={
                    data().payerTypeId != null ? String(data().payerTypeId) : ''
                  }
                  onChange={(val) => {
                    const id = parseInt(val, 10);
                    setFields('payerTypeId', id);
                    setFields(
                      'payerTypeDescription',
                      customerLTLSettings.cspSetup.ltlScacAccountTypes.payerTypes.find(
                        (term) => term.id === id,
                      )?.description,
                    );
                  }}
                  error={(() => {
                    const err = errors().payerTypeId;
                    if (err == null) return null;
                    if (Array.isArray(err)) return err[0] ?? null;
                    return typeof err === 'string' ? err : null;
                  })()}
                  dataTestId={formatDataTestId('CSP payer type', 'selectInput')}
                />
                <SelectInput
                  label="Term Type"
                  placeholder="Select Term Type"
                  menuItems={customerLTLSettings.cspSetup.ltlScacAccountTypes.termTypes.map(
                    (termType) => ({
                      label: termType.description,
                      value: String(termType.id),
                    }),
                  )}
                  value={
                    data().termTypeId != null ? String(data().termTypeId) : ''
                  }
                  onChange={(val) => {
                    const id = parseInt(val, 10);
                    setFields('termTypeId', id);
                    setFields(
                      'termTypeDescription',
                      customerLTLSettings.cspSetup.ltlScacAccountTypes.termTypes.find(
                        (term) => term.id === id,
                      )?.description,
                    );
                  }}
                  error={(() => {
                    const err = errors().termTypeId;
                    if (err == null) return null;
                    if (Array.isArray(err)) return err[0] ?? null;
                    return typeof err === 'string' ? err : null;
                  })()}
                  dataTestId={formatDataTestId('CSP term type', 'selectInput')}
                />
                <Show when={isBlanketAccount(props)}>
                  <Box class="flex gap-6">
                    <Box>
                      <Typography variant="subtitle2">Service Types</Typography>
                      {customerLTLSettings.cspSetup.ltlScacAccountTypes.serviceLevelTypes.map(
                        (serviceType) => {
                          return (
                            <Box class="ml-[-10px]">
                              <Checkbox
                                // name="serviceLevelTypes"
                                dataTestId="serviceTypeId-checkbox"
                                label={serviceType.displayName}
                                size="xs"
                                color="info"
                                checked={
                                  data().serviceLevelTypes?.some(
                                    (st) => st.id === serviceType.id,
                                  ) ?? false
                                }
                                onChange={(checked) => {
                                  const updated = checked
                                    ? [
                                        ...(data().serviceLevelTypes ?? []),
                                        {
                                          id: serviceType.id,
                                          displayName: serviceType.displayName,
                                          code: serviceType.code,
                                        },
                                      ]
                                    : (data().serviceLevelTypes ?? []).filter(
                                        (st) => st.id !== serviceType.id,
                                      );

                                  setFields('serviceLevelTypes', updated);
                                }}
                              />
                            </Box>
                          );
                        },
                      )}
                      <Show when={errors().serviceLevelTypes}>
                        <FormErrorComponent
                          error={errors().serviceLevelTypes}
                          showHelpertext
                        />
                      </Show>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">
                        Pricing Request Types
                      </Typography>
                      {customerLTLSettings.cspSetup.ltlScacAccountTypes.pricingRequestTypes
                        // PricingRequestTypeGet always includes id 99 (PricingNonspecific); it is never user-selectable.
                        .filter((i) => i.id !== -1 && i.id !== 99)
                        .map((pricingType) => {
                          return (
                            <Box class="ml-[-10px]">
                              <Checkbox
                                // name="pricingRequestTypes"
                                dataTestId="pricingRequestTypes-checkbox"
                                label={pricingType.description}
                                size="xs"
                                color="info"
                                checked={
                                  data().pricingRequestTypes?.some(
                                    (pt) => pt.id === pricingType.id,
                                  ) ?? false
                                }
                                onChange={(checked) => {
                                  const updated = checked
                                    ? [
                                        ...(data().pricingRequestTypes ?? []),
                                        {
                                          id: pricingType.id,
                                          description: pricingType.description,
                                        },
                                      ]
                                    : (data().pricingRequestTypes ?? []).filter(
                                        (pt) => pt.id !== pricingType.id,
                                      );
                                  setFields('pricingRequestTypes', updated);
                                }}
                              />
                            </Box>
                          );
                        })}
                      <Show when={errors().pricingRequestTypes}>
                        <FormErrorComponent
                          error={errors().pricingRequestTypes}
                          showHelpertext
                        />
                      </Show>
                    </Box>
                  </Box>
                </Show>
                <Show when={!isBlanketAccount(props)}>
                  <Stack spacing={0.5}>
                    <TextField
                      label="Bill To Name"
                      disabled={!overrideBillTo()}
                      value={data().billToName ?? ''}
                      onChange={(e: string) => setFields('billToName', e)}
                      dataTestId="customer-specific-pricing-edit-modal-bill-to-name-textInput"
                    />
                    <div class="flex items-center">
                      <Checkbox
                        label="Override Bill To Name"
                        checked={overrideBillTo()}
                        onChange={(checked) => {
                          setOverrideBillTo(checked);
                          if (!checked) {
                            setFields('billToName', originalBillToName());
                          }
                        }}
                        dataTestId="ltl-pricing-setup-edit-modal-override-bill-to-name-checkbox"
                      />
                    </div>
                    <Typography variant="body2" sxProps={{ marginTop: '8px' }}>
                      <i>
                        This is how the Bill To Name will appear on the BOL:
                      </i>
                      <br />
                      <b>{data().billToName} % Armstrong Transport Group</b>
                    </Typography>
                  </Stack>
                </Show>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
          <Show
            when={
              !displayTestLane() &&
              Boolean(data().accountNumber) &&
              Boolean(data().payerTypeId) &&
              Boolean(data().termTypeId)
            }
          >
            <Button
              label={'Proceed To Test Lane'}
              variant="contained"
              color="primary"
              onClick={() => {
                setDisplayTestLane(true);
              }}
              disabled={isSubmitting()}
              dataTestId="customer-specific-pricing-edit-modal-proceed-to-test-lane-button"
            />
          </Show>
          <Button
            type="submit"
            label={`${
              typeof props.selectedAccount?.id === 'number' &&
              Number.isFinite(props.selectedAccount.id)
                ? 'Update Account'
                : 'Add Account'
            }`}
            variant="contained"
            color="primary"
            isLoading={isSubmitting()}
            disabled={isSubmitting() || !testedLane()}
            dataTestId="customer-specific-pricing-edit-modal-save-account-button"
          />
        </Box>
      </form>
      <Show when={displayTestLane()}>
        <TestLane
          testedLane={testedLane}
          setTestedLane={setTestedLane}
          customerId={
            (props.customerId ??
              props.selectedCSPCustomer?.customerId) as number
          }
          scacAccount={
            {
              ...data(),
              carrierId:
                data().carrierId ?? carrierDetailsStore.carrierDetails?.id,
            } as ScacAccount
          }
          scacAccountTypes={() =>
            customerLTLSettings.cspSetup.ltlScacAccountTypes
          }
          changeTrackingSource={props.testLaneChangeTrackingSource}
        />
      </Show>
      <ConfirmationDialogBox
        dataTestId="AddNewEvaAccessIdConfirmationDialog"
        id="AddNewEvaAccessIdConfirmationDialog"
        title="You are about to save a new EVA Access ID.
                   Once you create it, this ID will be used for all future CSP setups
                   where an EVA Access ID is required.
                   Please confirm that an EVA Access ID has already been
                   created in LTL Go with the same name."
        onSubmit={handleConfirmEvaAccessId}
        onSubmitText="Continue"
        onCancel={() => {
          closeDialogBox('AddNewEvaAccessIdConfirmationDialog');
        }}
        isDialogOpen={checkIfDialogBoxIsOpen(
          'AddNewEvaAccessIdConfirmationDialog',
        )}
        closeDialogBox={(_reason) =>
          closeDialogBox('AddNewEvaAccessIdConfirmationDialog')
        }
      />
    </>
  );
};

export default LTLPricingSetupEditModal;
